import Link from "next/link";
import styles from "./Button.module.scss";
import {
  BUTTON_VARIANT,
  BUTTON_SIZE,
  BUTTON_VARIANTS,
  BUTTON_SIZES,
} from "./constants";

// Re-exported so callers need only one import:
//   import Button, { BUTTON_VARIANT } from "@/components/Common/Button";
export { BUTTON_VARIANT, BUTTON_SIZE, BUTTON_VARIANTS, BUTTON_SIZES };

/**
 * Shared Button — the reusable CTA primitive.
 *
 * Renders the right element for the job, which is the main reason this exists as
 * a component rather than a CSS mixin:
 *   - `href` starting with "/"  → `next/link` (client-side navigation)
 *   - `href` external or hash   → plain `<a>`
 *   - no `href`                 → `<button type=…>`
 * All three look identical, so a design change lands everywhere at once.
 *
 * ── Scope ────────────────────────────────────────────────────────────────────
 * ⚠️ Adopted by NEW work and by the landing pages. Existing components are
 * deliberately NOT migrated — see styles/_colors.scss for the reasoning. The
 * older `components/Button` stub is left in place because `pages/login` imports
 * it; do not repoint that import as a drive-by change.
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 *   <Button variant="filled" onClick={fn}>Request Quote</Button>
 *   <Button variant="outline" href="/contact-elchemy">Talk to us</Button>
 *   <Button variant="secondary" size="sm" block trailingIcon={<Arrow />}>Next</Button>
 *   <Button variant="filled" loading>Submitting…</Button>
 *
 * @param {"filled"|"outline"|"secondary"|"dark"|"light"|"ghostLight"|"text"} variant
 * @param {"sm"|"md"|"lg"} size
 * @param {boolean} block     Full width (mobile CTA stacks).
 * @param {boolean} rounded   Pill radius.
 * @param {boolean} loading   Shows a spinner and blocks interaction.
 * @param {string}  href      Present → renders a link instead of a button.
 */
export default function Button({
  children,
  variant = BUTTON_VARIANT.FILLED,
  size = BUTTON_SIZE.MD,
  block = false,
  rounded = false,
  nowrap = false,
  loading = false,
  disabled = false,
  leadingIcon,
  trailingIcon,
  href,
  type = "button",
  target,
  rel,
  className = "",
  ...rest
}) {
  // Dev-only guard. An unknown variant/size resolves to `undefined`, drops out of
  // the join, and renders an unstyled button with NO error — so it is surfaced here
  // rather than left to be spotted visually.
  if (process.env.NODE_ENV !== "production") {
    if (!BUTTON_VARIANTS.includes(variant)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Button] Unknown variant "${variant}". Use BUTTON_VARIANT.* — one of: ${BUTTON_VARIANTS.join(", ")}.`,
      );
    }
    if (!BUTTON_SIZES.includes(size)) {
      // eslint-disable-next-line no-console
      console.warn(
        `[Button] Unknown size "${size}". Use BUTTON_SIZE.* — one of: ${BUTTON_SIZES.join(", ")}.`,
      );
    }
  }

  const classes = [
    styles.btn,
    styles[variant],
    styles[size],
    block ? styles.block : "",
    rounded ? styles.rounded : "",
    nowrap ? styles.nowrap : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {!loading && leadingIcon && (
        <span className={styles.icon} aria-hidden="true">
          {leadingIcon}
        </span>
      )}
      {children}
      {trailingIcon && (
        <span className={styles.icon} aria-hidden="true">
          {trailingIcon}
        </span>
      )}
    </>
  );

  if (href) {
    const isInternal = href.startsWith("/");
    // A disabled link is not a thing in HTML, so it is expressed with ARIA and
    // the pointer-events guard in the stylesheet.
    const linkProps = {
      className: classes,
      target,
      // Any new tab gets noopener: without it the opened page can reach
      // window.opener and navigate this one.
      rel: rel ?? (target === "_blank" ? "noopener noreferrer" : undefined),
      "aria-disabled": disabled || loading ? "true" : undefined,
      ...rest,
    };

    return isInternal ? (
      <Link href={href} {...linkProps}>
        {content}
      </Link>
    ) : (
      <a href={href} {...linkProps}>
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      // Tells assistive tech the control is busy rather than just unclickable.
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
}
